import { IconStrokeWidth } from '@actr/icons';
import { IconInfoCircle } from '@actr/icons/info-circle';
import { Sizes } from '@actr/style';
import { ThemeColorValueImportance, ThemeColorValueTypes } from '@actr/style/src/specs/theme-v2';
import { FlexDirection, FlexJustify, FlexWrapper } from '@actr/ui/src/components/_atoms/flex-wrapper';
import { Text } from '@actr/ui/src/components/_atoms/text';
import { Tooltip } from '@actr/ui/src/components/tooltip';
import { formatMoney } from '@actr/utilities/src/currency';
import { DecodeImageQuality, getDecodeImage } from '@actr/utilities/src/url/decode-image';
import classNames from 'classnames';
import React, { FC } from 'react';

import ChannelDecoratedLink from '@/ui/channel-decorated-link';

import * as Styled from './style.css';

export type DestinationCardProps = {
  /** Название направления/города/страны */
  name: string;
  /** Описание направления/города/страны */
  description: string;
  /** Инормация для тултипа */
  info: string;
  /** URL изображения для карточки */
  image: string;
  /** Ссылка для перехода при клике */
  route?: string;
  /** Индекс элемента в списке (используется для Schema.org position) */
  imageIndex: number;
  /** Дополнительные CSS классы */
  className?: string;
  /** Включить микроразметку Schema.org */
  showSchema?: boolean;
  /** Фиксированная ширина изображения для мобильных */
  fixedImgWidth?: string;
  /** Минимальная стоимость предложения */
  minPrice: number;
};

const DestinationCard: FC<DestinationCardProps> = ({
  name,
  description,
  info,
  image,
  route,
  imageIndex,
  className,
  showSchema = false,
  fixedImgWidth,
  minPrice
}) => {
  const imageSrc = getDecodeImage(image, DecodeImageQuality.Q30);

  const cardContent = (
    <div className={classNames(Styled.City, className)}>
      <img
        className={Styled.CityImg({ fixedImgWidth: !!fixedImgWidth })}
        style={fixedImgWidth ? { '--city-img-mobile-width': fixedImgWidth } as React.CSSProperties : undefined}
        src={imageSrc}
        alt={name}
        itemProp={showSchema ? 'image' : undefined}
      />
      <FlexWrapper gap={Sizes.X4S} direction={FlexDirection.COL} className={Styled.Content}>
        <FlexWrapper direction={FlexDirection.COL}>
          <FlexWrapper gap={Sizes.X2S} justify={FlexJustify.SPACE_BETWEEN}>
            <Text font="title.m" tagProps={{ itemProp: showSchema ? 'name' : undefined }}>
              {name}
            </Text>
            <Tooltip content={(<Text>{info}</Text>)}>
              <IconInfoCircle
                className={Styled.IconInfoCircle}
                color={ThemeColorValueImportance.DARKER}
                colorType={ThemeColorValueTypes.CONTRAST}
                strokeWidth={IconStrokeWidth.BOLD}
              />
            </Tooltip>
          </FlexWrapper>
          <Text>
            {description}
          </Text>
        </FlexWrapper>
        <ChannelDecoratedLink className={Styled.Link} href={route} itemProp={showSchema ? 'url' : undefined}>
          <Text font="title.m" className={Styled.Price}>
            <span>Билет</span>
            {' '}
            от
            {' '}
            {formatMoney(minPrice)}
          </Text>
        </ChannelDecoratedLink>
      </FlexWrapper>
    </div>
  );

  if (showSchema) {
    return (
      <div itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
        <meta itemProp="position" content={(imageIndex + 1).toString()} />
        <div itemProp="item" itemScope itemType="https://schema.org/TouristDestination">
          {cardContent}
          {/* TODO Завести задачу на получение региона */}
          {/* <meta itemProp="address" content="" /> */}
        </div>
      </div>
    );
  }

  return cardContent;
};

export default DestinationCard;
